import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import Linechart from 'components/molecules/Linechart';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import {
    graphItemContainerClass,
    subHeaderClass,
    yAxisLabelClass,
} from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import dayjs from 'dayjs';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchPriceChangeTrendsChart } from 'hooks/priceChange/useFetchPriceChangeTrendsChartData';
import _ from 'lodash';
import { opportunityIdentificationTrendChartDownload, trendChartDownload } from 'parser/graphToExcelDownloadParser';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    calculateFontSize,
    fetchCurrency,
    fetchUnit,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
} from 'utils/helperMethods';

const currentYearSalesLegends = [
    { label: 'Year Ago', color: colorConstants.lightBlue },
    { label: 'Current Year', color: colorConstants.darkOrange },
    { label: 'Positive', color: colorConstants.limeGreen },
    { label: 'Negative', color: colorConstants.red },
];

const percentageChangeTitle = '% Change';

const barWidthMap: any = {
    4: 30,
    12: 20,
    26: 20,
    52: 11,
};

const chartHeight = calculateFontSize(window.innerWidth, 130);
const yAxisWidth = calculateFontSize(window.innerWidth, 80);

interface ITrendsChartPriceChange {
    index: number;
    expandedItem: any;
    stringifiedFilter: string;
    secondStartRange: string;
    secondEndRange: string;
    volTile: any;
    selectedKpi: any;
    selectedTimePeriodDropdown: any;
    exportTitle: string[][];
}

const TrendsChart = (props: ITrendsChartPriceChange) => {
    const [loading, setLoading] = useState(false);
    const {
        expandedItem,
        stringifiedFilter,
        secondEndRange,
        selectedTimePeriodDropdown,
        secondStartRange,
        volTile,
        selectedKpi,
        exportTitle,
    } = props;
    const {
        timePeriodSelection,
        idForRestOfComponent,
        idForRestOfComponentLoading,
        allCheckedAttribute,
        timeSelectionLabel,
    } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);

    const trendsChartRef = useRef<any>();
    const containerWidth = useContainerWidth({
        containerId: `trends-chart-growth-analysis`,
        buffer: yAxisWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides
    const {
        data: priceChangeSalesTrendData,
        loading: priceChangeSalesTrendLoading,
        error: priceChangeSalesTrendError,
        setLoading: trendsChartSetLoading,
        fetchPriceChangeTrendsChartData,
    } = useFetchPriceChangeTrendsChart();

    const { error: applyFilterError, applyFilterMutate } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete,
        onError: onApplyFilterError,
    });

    function onApplyFilterError() {
        trendsChartSetLoading(false);
    }

    const combinedError = priceChangeSalesTrendError || applyFilterError;
    const combinedLoading = loading || priceChangeSalesTrendLoading || idForRestOfComponentLoading;

    useEffect(() => {
        prepareApplyFilterVariableAndExecute();
        setLoading(true);
    }, [
        expandedItem,
        timePeriodSelection,
        volTile,
        secondEndRange,
        secondStartRange,
        selectedTimePeriodDropdown,
        selectedKpi,
    ]);

    function prepareApplyFilterVariableAndExecute() {
        trendsChartSetLoading(true);
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        if (expandedItem.dimension === 'dim1') {
            tempApplyFilterVariable.jsonstring_dim1.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        } else {
            tempApplyFilterVariable.jsonstring_dim2.inclusion = [
                {
                    ...expandedItem,
                },
            ];
        }
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        applyFilterMutate({ variables: tempApplyFilterVariable });
    }

    const { lineChartResult = null, barChartResult = null } = priceChangeSalesTrendData || {};
    const barWidth = barWidthMap[lineChartResult?.data?.length] || 11;

    //called when apply filter call has completed
    function onApplyFilterComplete(payload: any) {
        const idForRestOfComponent = JSON.parse(stringifiedFilter);
        const tempApplyFilterResponse = _.cloneDeep(idForRestOfComponent);
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (idForRestOfComponent.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(idForRestOfComponent.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (idForRestOfComponent.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(idForRestOfComponent.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        const stringifiedApplyFilterResponse = JSON.stringify(tempApplyFilterResponse);
        setLoading(false);
        fetchTrendsChart(stringifiedApplyFilterResponse);
    }

    function fetchTrendsChart(stringifiedFilter: string) {
        const baselineTimePeriod = {
            start_timestamp: dayjs(secondStartRange, appConfig?.commonConfig.dateFormat).subtract(6, 'day').unix(),
            end_timestamp: dayjs(secondEndRange, appConfig?.commonConfig.dateFormat).subtract(6, 'day').unix(),
        };

        fetchPriceChangeTrendsChartData({
            variables: {
                dimension: expandedItem?.dimension,
                attributeType: expandedItem?.attributeType,
                dateRange: [timePeriodSelection, JSON.stringify(baselineTimePeriod)],
                filters: stringifiedFilter,
                limit: 52,
                offset: 0,
                comparisonPeriodType: selectedTimePeriodDropdown?.value,
                metricType: volTile?.dataKey,
                priceType: selectedKpi?.value,
            },
        });
    }

    // useEffect(() => {
    //     fetchTrendsChart(stringifiedFilter);
    // }, [secondEndRange, secondStartRange]);

    const currency = fetchUnit(appConfig, volTile?.dataKey === 'units' ? 'currency' : 'volume');
    const graphLabel = `${selectedKpi?.label} ${currency}`;
    const trendsChartExportData = useMemo(() => {
        return trendChartDownload(lineChartResult?.data, barChartResult?.data);
    }, [lineChartResult?.data, barChartResult?.data]);

    const mainLabel = `Trend Chart - ${expandedItem?.attributeExId}`;

    // IF ERROR RETURN ERROR MESSAGE
    if (combinedError && !combinedLoading && !priceChangeSalesTrendData)
        return <ErrorBox msg={String(priceChangeSalesTrendError)} />;

    // IF LOADING RETURN LOADER
    if (combinedLoading)
        return (
            <div style={{ height: 370, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );
    return (
        <div className={`${graphItemContainerClass} mt-4`} id="trends-chart-growth-analysis">
            <div className="flex justify-between items-center">
                <p className={subHeaderClass}>{mainLabel}</p>
                <div className="flex items-center gap-2">
                    <CopyToClipBoard refOfComponent={trendsChartRef} />

                    <DownloadExcel
                        title={'trendsChartData'}
                        titleArray={[[mainLabel], ...exportTitle]}
                        excelData={trendsChartExportData.data}
                        headers={trendsChartExportData.headers}
                    />
                </div>
            </div>
            <div className=" mb-1">
                <p className={`${yAxisLabelClass} relative mt-[-16px] top-5`}>{graphLabel}</p>
                <Legend data={currentYearSalesLegends} />
            </div>

            <div ref={trendsChartRef}>
                <div className="ml-1 flex">
                    <Linechart
                        padding={[0, 0, 12, 0]}
                        xAxisConfig={false}
                        YGrid={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-line-y-${props.index}`}
                        fields={['period', 'Year Ago', 'Current Year']}
                        minBound={lineChartResult?.minVal}
                        maxBound={lineChartResult?.maxVal}
                        width={yAxisWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => val}
                        yAxisLabelFormatter={(val: any) => formatNumWithKMBAlt(val, 2, 2)}
                        groupedChartColor={[colorConstants.lightBlue, colorConstants.darkOrange]}
                        colorKey={'color'}
                        disableIntervalLabel
                    />
                    <Linechart
                        padding={[5, 0, 0, 0]}
                        dataset={lineChartResult?.data || []}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-line-${props.index}`}
                        fields={['period', 'Year Ago', 'Current Year']}
                        minBound={lineChartResult?.minVal}
                        maxBound={lineChartResult?.maxVal}
                        width={containerWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => val}
                        groupedChartColor={[colorConstants.lightBlue, colorConstants.darkOrange]}
                        colorKey={'color'}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis, val, from) => {
                            return {
                                name: from,
                                value: (volTile?.dataKey === 'units' ? currency : '') + formatNumWithKMBAlt(val, 2, 2),
                            };
                        }}
                    />
                </div>
                <p className={`${yAxisLabelClass} mb-2 !top-0`}>{percentageChangeTitle}</p>
                <div className="ml-1 flex">
                    <Barchart
                        padding={[0, 0, 12, 0]}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-column-y-${props.index}`}
                        fields={['category', 'sales']}
                        minBound={barChartResult?.minVal}
                        maxBound={barChartResult?.maxVal}
                        width={yAxisWidth} //subtracting the padding
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMB(val) + '%';
                        }}
                        xAxisConfig={false}
                        YGrid={false}
                        colorKey={'color'}
                        disableIntervalLabel
                    />
                    <Barchart
                        barWidth={barWidth}
                        dataset={barChartResult?.data || []}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-column-${props.index}`}
                        fields={['category', 'sales']}
                        minBound={barChartResult?.minVal}
                        maxBound={barChartResult?.maxVal}
                        width={containerWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => val}
                        intervalStyle={{ radius: [6, 6, 0, 0] }}
                        colorKey={'color'}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: percentageChangeTitle,
                                value: formatNumWithKMBAlt(val, 2, 2) + '%',
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TrendsChart;
